/**
 * Atlas selfie-fd demo logic with UX helpers for guided alignment.
 * @see https://www.xshq.net/samples/selfie-fd.html
 */
import '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import * as faceDetection from '@tensorflow-models/face-detection';
import type { FaceDetector } from '@tensorflow-models/face-detection';
import type { GuideTone, LivenessSnapshot, SelfieFdSnapshotCallback } from './selfie-fd.model';

export type { GuideTone, LivenessSnapshot, SelfieFdSnapshotCallback } from './selfie-fd.model';

export const CAPTURE_WIDTH = 640;
export const CAPTURE_HEIGHT = 480;
export const REQUIRED_STABLE_FRAMES = 15;
export const OVERLAY_SIZE = 500;

const MIN_WIDTH = 640;
const MIN_HEIGHT = 480;
const IDEAL_WIDTH = 1280;
const IDEAL_HEIGHT = 720;
const LOCAL_MEDIAPIPE_PATH = '/mediapipe/face_detection';
const DEFAULT_MEDIAPIPE_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_detection';
const GUIDE_CX = 250;
const GUIDE_CY = 250;

function isMobileDevice(): boolean {
	return /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent);
}

function buildCameraVideoConstraints(
	facingMode: 'user' | 'environment' = 'user',
): MediaTrackConstraints {
	if (isMobileDevice()) {
		return {
			facingMode,
			width: { ideal: 640 },
			height: { ideal: 480 },
		};
	}

	return {
		facingMode,
		width: { min: MIN_WIDTH, ideal: IDEAL_WIDTH },
		height: { min: MIN_HEIGHT, ideal: IDEAL_HEIGHT },
	};
}

function mediapipeSolutionPaths(): readonly string[] {
	const cdn =
		(import.meta.env.VITE_MEDIAPIPE_CDN as string | undefined)?.trim() || DEFAULT_MEDIAPIPE_CDN;
	const paths = [cdn];
	if (cdn !== LOCAL_MEDIAPIPE_PATH) {
		paths.push(LOCAL_MEDIAPIPE_PATH);
	}
	return paths;
}

export function resolveCameraAccessErrorMessage(error: unknown): string {
	if (error && typeof error === 'object' && 'name' in error) {
		const name = String((error as DOMException).name);
		switch (name) {
			case 'NotAllowedError':
			case 'PermissionDeniedError':
				return 'Camera permission denied. Please allow camera access in your browser settings.';
			case 'NotFoundError':
			case 'DevicesNotFoundError':
				return 'No camera found on your device.';
			case 'NotReadableError':
			case 'TrackStartError':
				return 'Camera is already in use by another application.';
			case 'OverconstrainedError':
				return 'Camera does not support the requested settings.';
			default:
				break;
		}
	}
	return 'Unable to access camera. Please check permissions and try again.';
}

export function logCameraAccessError(error: unknown): void {
	console.error('Camera access error:', error);
}

interface FaceBox {
	xMin: number;
	yMin: number;
	width: number;
	height: number;
}

export class SelfieFdEngine {
	private detector: FaceDetector | null = null;
	private stream: MediaStream | null = null;
	private detectionLoopRunning = false;
	private animationFrameId: number | null = null;
	private lastFace: { box: FaceBox } | null = null;
	private movementDetected = false;
	private previousFaceX: number | null = null;
	stableFrames = 0;

	private video: HTMLVideoElement | null = null;
	private overlay: HTMLCanvasElement | null = null;
	private ctx: CanvasRenderingContext2D | null = null;
	private attached = false;
	private detectorInitPromise: Promise<void> | null = null;
	private snapshotListener: SelfieFdSnapshotCallback | null = null;
	private facingMode: 'user' | 'environment' = 'user';

	get isAttached(): boolean {
		return this.attached;
	}

	onSnapshot(listener: SelfieFdSnapshotCallback | null): void {
		this.snapshotListener = listener;
	}

	attach(video: HTMLVideoElement, overlay: HTMLCanvasElement): void {
		const ctx = overlay.getContext('2d');
		if (!ctx) {
			throw new Error('No 2D context');
		}
		this.video = video;
		this.overlay = overlay;
		this.ctx = ctx;
		this.attached = true;
	}

	detach(): void {
		void this.stopCamera();
		this.video = null;
		this.overlay = null;
		this.ctx = null;
		this.attached = false;
		this.snapshotListener = null;
	}

	private requireElements(): {
		video: HTMLVideoElement;
		overlay: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	} {
		if (!this.attached || !this.video || !this.overlay || !this.ctx) {
			throw new Error('SelfieFdEngine is not attached to video and overlay elements.');
		}
		return { video: this.video, overlay: this.overlay, ctx: this.ctx };
	}

	resetState(): void {
		if (!this.attached || !this.ctx || !this.overlay) {
			return;
		}
		this.lastFace = null;
		this.movementDetected = false;
		this.previousFaceX = null;
		this.stableFrames = 0;
		this.emitSnapshot(this.idleSnapshot('Starting camera…'));
		this.ctx.clearRect(0, 0, this.overlay.width, this.overlay.height);
		this.drawGuideOval('idle');
	}

	async stopCamera(): Promise<void> {
		this.detectionLoopRunning = false;

		if (this.animationFrameId !== null) {
			cancelAnimationFrame(this.animationFrameId);
			this.animationFrameId = null;
		}

		if (this.stream) {
			this.stream.getTracks().forEach((track) => track.stop());
			this.stream = null;
		}

		if (this.video) {
			this.video.srcObject = null;
		}
	}

	async resetAll(): Promise<void> {
		await this.stopCamera();
		this.resetState();
	}

	/** Warm up MediaPipe before the camera starts. Safe to call multiple times. */
	preloadDetector(): Promise<void> {
		if (this.detector) {
			return Promise.resolve();
		}

		this.detectorInitPromise ??= this.initialiseDetector().catch((error) => {
			this.detectorInitPromise = null;
			throw error;
		});

		return this.detectorInitPromise;
	}

	async initialiseDetector(): Promise<void> {
		if (this.detector) {
			return;
		}

		const model = faceDetection.SupportedModels.MediaPipeFaceDetector;
		const solutionPaths = mediapipeSolutionPaths();
		let lastError: unknown;

		for (const solutionPath of solutionPaths) {
			try {
				this.detector = await faceDetection.createDetector(model, {
					runtime: 'mediapipe',
					solutionPath,
				});
				return;
			} catch (err) {
				lastError = err;
				this.detector = null;
				console.warn(`MediaPipe face detector failed for ${solutionPath}`, err);
			}
		}

		throw lastError instanceof Error
			? lastError
			: new Error('Face detector could not be initialised');
	}

	setFacingMode(mode: 'user' | 'environment'): void {
		this.facingMode = mode;
	}

	async startCamera(): Promise<void> {
		if (!this.attached || !this.video || !this.overlay || !this.ctx) {
			throw new Error('SelfieFdEngine must be attached to video and overlay elements first.');
		}

		try {
			const detectorPromise = this.preloadDetector();
			await this.resetAll();

			this.stream = await navigator.mediaDevices.getUserMedia({
				video: buildCameraVideoConstraints(this.facingMode),
				audio: false,
			});

			this.video.srcObject = this.stream;
			await this.video.play();

			if (!this.detector) {
				this.emitSnapshot(this.idleSnapshot('Loading face detection…'));
			}

			await detectorPromise;

			this.detectionLoopRunning = true;
			this.startDetectionLoop();
			this.emitSnapshot(this.idleSnapshot('Position your face in the oval'));
		} catch (error) {
			logCameraAccessError(error);
			const hint =
				this.stream && !this.detector
					? 'Could not load face detection. Check your connection and try again.'
					: resolveCameraAccessErrorMessage(error);
			this.emitSnapshot(this.idleSnapshot(hint));
			throw error;
		}
	}

	drawGuideOval(tone: GuideTone): void {
		const { ctx } = this.requireElements();
		const colors: Record<GuideTone, string> = {
			idle: 'rgba(255,255,255,0.55)',
			warn: '#fc8e00',
			progress: '#22c55e',
			ready: '#16a34a',
		};

		ctx.strokeStyle = colors[tone];
		ctx.lineWidth = tone === 'ready' ? 4 : 3;
		ctx.beginPath();
		ctx.ellipse(GUIDE_CX, GUIDE_CY, 140, 180, 0, 0, Math.PI * 2);
		ctx.stroke();
	}

	private getMirroredFaceCenter(box: FaceBox): { x: number; y: number } {
		const { video, overlay } = this.requireElements();
		const scaleX = overlay.width / video.videoWidth;
		const scaleY = overlay.height / video.videoHeight;

		let x = box.xMin * scaleX;
		let y = box.yMin * scaleY;
		let width = box.width * scaleX;
		let height = box.height * scaleY;

		width *= 1.8;
		height *= 2.2;
		x -= width * 0.22;
		y -= height * 0.18;

		const mirroredX = overlay.width - x - width;
		const clampedX = Math.max(
			width / 2,
			Math.min(mirroredX + width / 2, overlay.width - width / 2),
		);
		const clampedY = Math.max(
			height / 2,
			Math.min(y + height / 2, overlay.height - height / 2),
		);

		return { x: clampedX, y: clampedY };
	}

	drawFaceOval(box: FaceBox, aligned: boolean): void {
		const { video, overlay, ctx } = this.requireElements();
		const { x, y } = this.getMirroredFaceCenter(box);
		const scaleX = overlay.width / video.videoWidth;
		const scaleY = overlay.height / video.videoHeight;
		const width = box.width * scaleX * 1.8;
		const height = box.height * scaleY * 2.2;

		ctx.strokeStyle = aligned ? '#22c55e' : 'rgba(252, 142, 0, 0.85)';
		ctx.lineWidth = aligned ? 3 : 2;
		ctx.setLineDash(aligned ? [] : [6, 4]);
		ctx.beginPath();
		ctx.ellipse(x, y, width / 2, height / 2, 0, 0, Math.PI * 2);
		ctx.stroke();
		ctx.setLineDash([]);
	}

	/** Arrow from face toward guide center (mirrored preview). */
	private drawAlignmentCue(box: FaceBox): void {
		const face = this.getMirroredFaceCenter(box);
		const dx = GUIDE_CX - face.x;
		const dy = GUIDE_CY - face.y;
		const dist = Math.hypot(dx, dy);
		if (dist < 24) {
			return;
		}

		const ux = dx / dist;
		const uy = dy / dist;
		const startX = face.x + ux * 28;
		const startY = face.y + uy * 28;
		const endX = GUIDE_CX - ux * 36;
		const endY = GUIDE_CY - uy * 36;

		const { ctx } = this.requireElements();
		ctx.strokeStyle = 'rgba(255,255,255,0.9)';
		ctx.fillStyle = 'rgba(255,255,255,0.9)';
		ctx.lineWidth = 2;
		ctx.beginPath();
		ctx.moveTo(startX, startY);
		ctx.lineTo(endX, endY);
		ctx.stroke();

		const head = 10;
		ctx.beginPath();
		ctx.moveTo(endX, endY);
		ctx.lineTo(endX - ux * head - uy * head * 0.5, endY - uy * head + ux * head * 0.5);
		ctx.lineTo(endX - ux * head + uy * head * 0.5, endY - uy * head - ux * head * 0.5);
		ctx.closePath();
		ctx.fill();
	}

	faceIsCentered(box: FaceBox): boolean {
		const { video } = this.requireElements();
		const centerX = box.xMin + box.width / 2;
		const centerY = box.yMin + box.height / 2;
		const deltaX = Math.abs(centerX - video.videoWidth / 2);
		const deltaY = Math.abs(centerY - video.videoHeight / 2);
		return deltaX < 120 && deltaY < 120;
	}

	faceIsLargeEnough(box: FaceBox): boolean {
		const { video } = this.requireElements();
		const faceArea = box.width * box.height;
		const frameArea = video.videoWidth * video.videoHeight;
		return faceArea / frameArea > 0.1;
	}

	detectMovement(box: FaceBox): void {
		const currentX = box.xMin;
		if (this.previousFaceX !== null) {
			const diff = Math.abs(currentX - this.previousFaceX);
			if (diff > 3) {
				this.movementDetected = true;
			}
		}
		this.previousFaceX = currentX;
	}

	isGoodLivenessFrame(box: FaceBox): boolean {
		return (
			this.movementDetected && this.faceIsCentered(box) && this.faceIsLargeEnough(box)
		);
	}

	private buildAlignmentHint(box: FaceBox): string | null {
		const { video } = this.requireElements();
		const centerX = box.xMin + box.width / 2;
		const centerY = box.yMin + box.height / 2;
		const deltaX = centerX - video.videoWidth / 2;
		const deltaY = centerY - video.videoHeight / 2;
		const parts: string[] = [];

		if (deltaX > 40) {
			parts.push('right');
		} else if (deltaX < -40) {
			parts.push('left');
		}
		if (deltaY > 40) {
			parts.push('down');
		} else if (deltaY < -40) {
			parts.push('up');
		}

		if (parts.length === 0) {
			return null;
		}
		if (parts.length === 1) {
			return `Move slightly ${parts[0]}`;
		}
		return `Move slightly ${parts[0]} and ${parts[1]}`;
	}

	private buildSnapshot(box: FaceBox | null): LivenessSnapshot {
		if (!box) {
			return {
				faceDetected: false,
				centered: false,
				largeEnough: false,
				movementDetected: this.movementDetected,
				stableFrames: 0,
				canCapture: false,
				primaryHint: 'Position your face in the oval',
				guideTone: 'idle',
			};
		}

		const centered = this.faceIsCentered(box);
		const largeEnough = this.faceIsLargeEnough(box);
		const canCapture = this.stableFrames >= REQUIRED_STABLE_FRAMES;

		let primaryHint = 'Align face and move naturally';
		let guideTone: GuideTone = 'warn';

		if (canCapture) {
			primaryHint = 'Ready — tap Capture Photo';
			guideTone = 'ready';
		} else if (this.stableFrames > 0) {
			primaryHint = `Hold still (${this.stableFrames}/${REQUIRED_STABLE_FRAMES})`;
			guideTone = 'progress';
		} else if (!largeEnough) {
			primaryHint = 'Move closer to the camera';
			guideTone = 'warn';
		} else if (!centered) {
			primaryHint = this.buildAlignmentHint(box) ?? 'Center your face in the oval';
			guideTone = 'warn';
		} else if (!this.movementDetected) {
			primaryHint = 'Slowly turn your head left or right';
			guideTone = 'warn';
		}

		return {
			faceDetected: true,
			centered,
			largeEnough,
			movementDetected: this.movementDetected,
			stableFrames: this.stableFrames,
			canCapture,
			primaryHint,
			guideTone,
		};
	}

	private idleSnapshot(hint: string): LivenessSnapshot {
		return {
			faceDetected: false,
			centered: false,
			largeEnough: false,
			movementDetected: false,
			stableFrames: 0,
			canCapture: false,
			primaryHint: hint,
			guideTone: 'idle',
		};
	}

	private emitSnapshot(snapshot: LivenessSnapshot): void {
		this.snapshotListener?.(snapshot);
	}

	startDetectionLoop(): void {
		const detect = async (): Promise<void> => {
			if (!this.detectionLoopRunning) {
				return;
			}

			const { video, overlay, ctx } = this.requireElements();
			ctx.clearRect(0, 0, overlay.width, overlay.height);

			if (this.detector && video.readyState >= 2) {
				const faces = await this.detector.estimateFaces(video, {
					flipHorizontal: false,
				});

				if (faces.length > 0) {
					const box = faces[0].box;
					this.lastFace = faces[0];
					this.detectMovement(box);

					const centered = this.faceIsCentered(box);
					const largeEnough = this.faceIsLargeEnough(box);
					const goodFrame = this.isGoodLivenessFrame(box);

					if (goodFrame) {
						this.stableFrames = Math.min(
							this.stableFrames + 1,
							REQUIRED_STABLE_FRAMES,
						);
					} else {
						this.stableFrames = 0;
					}

					const snapshot = this.buildSnapshot(box);
					const aligned = centered && this.movementDetected && largeEnough;

					if (aligned) {
						this.drawGuideOval(snapshot.guideTone);
					} else {
						this.drawFaceOval(box, false);
						this.drawGuideOval('warn');
						if (!centered) {
							this.drawAlignmentCue(box);
						}
					}
					this.emitSnapshot(snapshot);
				} else {
					this.lastFace = null;
					this.stableFrames = 0;
					const snapshot = this.buildSnapshot(null);
					this.drawGuideOval('idle');
					this.emitSnapshot(snapshot);
				}
			} else {
				this.drawGuideOval('idle');
			}

			this.animationFrameId = requestAnimationFrame(() => void detect());
		};

		void detect();
	}

	captureSelfie(): Promise<Blob> {
		if (this.stableFrames < REQUIRED_STABLE_FRAMES) {
			return Promise.reject(new Error('Not ready to capture'));
		}

		const { video } = this.requireElements();

		return new Promise<Blob>((resolve, reject) => {
			const exportCanvas = document.createElement('canvas');
			exportCanvas.width = CAPTURE_WIDTH;
			exportCanvas.height = CAPTURE_HEIGHT;
			const exportCtx = exportCanvas.getContext('2d');
			if (!exportCtx) {
				reject(new Error('No 2D context'));
				return;
			}

			const vw = video.videoWidth;
			const vh = video.videoHeight;
			const targetAspect = CAPTURE_WIDTH / CAPTURE_HEIGHT;

			let sx: number;
			let sy: number;
			let sw: number;
			let sh: number;
			if (vw / vh > targetAspect) {
				sh = vh;
				sw = vh * targetAspect;
				sx = (vw - sw) / 2;
				sy = 0;
			} else {
				sw = vw;
				sh = vw / targetAspect;
				sx = 0;
				sy = (vh - sh) / 2;
			}

			exportCtx.translate(CAPTURE_WIDTH, 0);
			exportCtx.scale(-1, 1);
			exportCtx.drawImage(video, sx, sy, sw, sh, 0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);

			exportCanvas.toBlob(
				(blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode PNG'))),
				'image/png',
			);
		});
	}
}
